/**
 * Tenant-wide master switches for notifications, as one compact strip.
 *
 * The email switch is a kill switch: with it off, the emit pipeline rejects the
 * `email` and `digest` channels for every recipient of this tenant before
 * anything reaches the email queue, whatever the per-event configuration below
 * says. Bells keep working. Switching either kill switch off asks for
 * confirmation first and applies immediately — these are the controls you reach
 * for during an incident, so they must not sit behind the grid's Save button.
 *
 * Each switch's explanation is on hover so the strip stays one line. The one
 * exception is email being off: that is an incident state, so it is spelled out
 * for as long as it lasts.
 */
import { useRef, useState } from 'react';
import type { NotificationSettingsUpdate } from '../../../../lib/api';
import { Label } from '../../../ui/label';
import { Switch } from '../../../ui/switch';
import { Badge } from '../../../ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../ui/alert-dialog';
import { Bell, Loader2, Mail, MailX, Newspaper, type LucideIcon } from 'lucide-react';
import { EMAIL_STOPPED_TEXT, EMAIL_STOPPED_TITLE, type MasterSwitches } from './types';

/** Which switch the confirmation dialog is currently guarding. */
type GuardedSwitch = 'emails' | 'bell' | 'digest';

const GUARD_COPY: Record<GuardedSwitch, { title: string; body: string; confirm: string }> = {
  emails: {
    title: 'Stop all notification email for this tenant?',
    body:
      'No notification email or digest will be sent to anyone in this tenant — '
      + 'customer or supplier — while this is off. Per-event email settings, '
      + 'per-user subscriptions and digests are all overridden. In-app bells '
      + 'keep working, so nothing is lost, but nobody is emailed about a late '
      + 'delivery, a dispute or an expiring document until you switch it back '
      + 'on. Invitations and password resets are unaffected.',
    confirm: 'Stop all notification email',
  },
  bell: {
    title: 'Stop all in-app notifications for this tenant?',
    body:
      'No new in-app notification will be created for anyone in this tenant '
      + 'while this is off. Users keep the notifications they already have, but '
      + 'nothing new appears in the bell until you switch it back on.',
    confirm: 'Stop in-app notifications',
  },
  digest: {
    title: 'Turn off the daily digest for this tenant?',
    body: 'Events will reach people by bell only while this is off, and those emails are not sent later.',
    confirm: 'Turn off the daily digest',
  },
};

const BELL_GUARD_ONE_DAILY =
  'The daily digest rides on the bell. With the Bell off, events follow their own frequency.';

const DIGEST_HINT =
  'Controls all batched email: the daily digest, summaries, and hourly and weekly batches.';
const DIGEST_HINT_ONE_DAILY =
  ' Events reach people by bell only while this is off, and those emails are not sent later.';

interface MasterSwitchProps {
  id: string;
  label: string;
  icon: LucideIcon;
  /**
   * What the switch does: on hover, on the wrapper because a disabled switch gets
   * none, and as the switch's description for assistive tech.
   */
  hint: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function MasterSwitch({
  id,
  label,
  icon: Icon,
  hint,
  checked,
  disabled,
  onCheckedChange,
}: MasterSwitchProps) {
  return (
    <div className="flex items-center gap-2" title={hint}>
      <Icon className="h-4 w-4 text-muted-foreground" />
      <Label htmlFor={id} className="font-medium">{label}</Label>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        aria-describedby={`${id}-hint`}
        onCheckedChange={onCheckedChange}
      />
      <span id={`${id}-hint`} className="sr-only">{hint}</span>
      <span aria-hidden="true" className="w-7 text-xs font-medium text-muted-foreground">
        {checked ? 'ON' : 'OFF'}
      </span>
    </div>
  );
}

interface NotificationMasterCardProps {
  master: MasterSwitches;
  canConfigure: boolean;
  /**
   * CR-8217: the email, bell and digest switches apply to the whole
   * installation, so only Helix and customer users may change them.
   */
  canSetInstallSwitches?: boolean;
  /** `meta.one_daily_digest`: the company is on the one daily digest. */
  oneDailyDigest?: boolean;
  saving: boolean;
  /** Applies a settings patch immediately and toasts `message` on success. */
  onChange: (patch: NotificationSettingsUpdate, message: string) => void;
  /** The dedicated kill-switch write. */
  onEmailsEnabledChange: (enabled: boolean) => void;
}

export function NotificationMasterCard({
  master,
  canConfigure,
  canSetInstallSwitches = true,
  oneDailyDigest = false,
  saving,
  onChange,
  onEmailsEnabledChange,
}: NotificationMasterCardProps) {
  const [guard, setGuard] = useState<GuardedSwitch | null>(null);
  // Set when a guard opens: no trigger to hand focus back to, and `guard` is
  // already null by the time the dialog closes.
  const guardedSwitch = useRef<GuardedSwitch | null>(null);

  const openGuard = (which: GuardedSwitch) => {
    guardedSwitch.current = which;
    setGuard(which);
  };

  const handleEmails = (enabled: boolean) => {
    if (!enabled) {
      openGuard('emails');
      return;
    }
    onEmailsEnabledChange(true);
  };

  const handleBell = (enabled: boolean) => {
    if (!enabled) {
      openGuard('bell');
      return;
    }
    onChange({ bell_enabled: true }, 'In-app notifications are back on');
  };

  const applyDigest = (enabled: boolean) =>
    onChange(
      { digest_enabled: enabled },
      enabled ? 'Daily digest switched on' : 'Daily digest switched off',
    );

  // With the Bell off, what the digest guard warns about is not what happens.
  const digestGuarded = oneDailyDigest && master.bellEnabled;

  const handleDigest = (enabled: boolean) => {
    if (!enabled && digestGuarded) {
      openGuard('digest');
      return;
    }
    applyDigest(enabled);
  };

  const confirmGuard = () => {
    if (guard === 'emails') {
      onEmailsEnabledChange(false);
    } else if (guard === 'bell') {
      onChange({ bell_enabled: false }, 'In-app notifications are now stopped for this tenant');
    } else if (guard === 'digest') {
      applyDigest(false);
    }
    setGuard(null);
  };

  const copy = guard ? GUARD_COPY[guard] : null;
  const installDisabled = !canConfigure || saving || !canSetInstallSwitches;
  const digestHint = (master.emailsEnabled ? '' : 'Already stopped by the Email switch. ')
    + DIGEST_HINT
    + (digestGuarded ? DIGEST_HINT_ONE_DAILY : '');
  // With Email stopped too, nothing is emailed one by one either.
  const bellGuardNote = guard === 'bell' && oneDailyDigest && master.emailsEnabled
    ? ` ${BELL_GUARD_ONE_DAILY}`
    : '';

  return (
    <>
      <div className="space-y-2">
        <div
          className={`flex flex-wrap items-center gap-x-8 gap-y-2 rounded-lg border px-4 py-2.5 ${
            master.emailsEnabled ? '' : 'border-destructive bg-destructive/5'
          }`}
        >
          {/* MASTER EMAIL KILL SWITCH */}
          <MasterSwitch
            id="notif-master-emails"
            label="Email"
            icon={master.emailsEnabled ? Mail : MailX}
            hint="Emails and digests send according to each event's settings."
            checked={master.emailsEnabled}
            disabled={installDisabled}
            onCheckedChange={handleEmails}
          />
          <MasterSwitch
            id="notif-master-bell"
            label="Bell"
            icon={Bell}
            hint="Creates bell notifications for this tenant."
            checked={master.bellEnabled}
            disabled={installDisabled}
            onCheckedChange={handleBell}
          />
          <MasterSwitch
            id="notif-master-digest"
            label="Daily digest"
            icon={Newspaper}
            hint={digestHint}
            checked={master.digestEnabled && master.emailsEnabled}
            disabled={installDisabled || !master.emailsEnabled}
            onCheckedChange={handleDigest}
          />
          {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!master.emailsEnabled && (
            <Badge variant="destructive">{EMAIL_STOPPED_TITLE}</Badge>
          )}
        </div>
        {!master.emailsEnabled && (
          <p className="text-sm text-muted-foreground">{EMAIL_STOPPED_TEXT}</p>
        )}
      </div>

      <AlertDialog open={guard !== null} onOpenChange={(open) => !open && setGuard(null)}>
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            document.getElementById(`notif-master-${guardedSwitch.current}`)?.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{copy?.title}</AlertDialogTitle>
            <AlertDialogDescription>{copy?.body}{bellGuardNote}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmGuard}>{copy?.confirm}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
